import { validateFieldsSchema } from "@repo/adaptive-form/schema";
import { type FC, type ReactNode, useMemo } from "react";
import { useAuthorizedUser } from "src/features/auth/useAuthorizedUser";
import {
  isValidUITextDictionaryId,
  UITextProvider,
} from "src/features/uiTexts";
import type { ValidatedWorkspaceGlobals } from "src/features/workspaceGlobals";
import { CurrentWorkspaceGlobalsProvider } from "src/features/workspaceGlobals/CurrentWorkspaceGlobalsProvider";
import { unsafeValidateChatConfigSchema } from "src/jsonSchema/chatConfigSchema";
import { unsafeValidateDocumentConfigSchema } from "src/jsonSchema/documentConfigSchema";
import { unsafeValidateKarteSchema } from "src/jsonSchema/karteSchema";
import { unsafeValidateMessagingConfigSchema } from "src/jsonSchema/messagingConfigSchema";
import { ErrorScreen } from "../ErrorScreen";

type Props = {
  children: ReactNode;
};

export const WorkspaceGlobalsGuard: FC<Props> = ({ children }) => {
  const { currentWorkspace } = useAuthorizedUser();

  // NOTE: バックエンドから得られる schema は、正しい形式のデータが入っている前提で、例外処理はしない
  const customerFieldsSchema = useMemo(() => {
    if (!currentWorkspace.activeCustomerFieldsSchema) {
      return null;
    }

    return {
      id: currentWorkspace.activeCustomerFieldsSchema.id,
      schema: validateFieldsSchema(
        currentWorkspace.activeCustomerFieldsSchema.schema,
      ),
    };
  }, [currentWorkspace.activeCustomerFieldsSchema]);

  const customerVisitFieldsSchema = useMemo(() => {
    if (!currentWorkspace.activeCustomerVisitFieldsSchema) {
      return null;
    }

    return {
      id: currentWorkspace.activeCustomerVisitFieldsSchema.id,
      schema: validateFieldsSchema(
        currentWorkspace.activeCustomerVisitFieldsSchema.schema,
      ),
    };
  }, [currentWorkspace.activeCustomerVisitFieldsSchema]);

  const karteSchemaSeries = useMemo(() => {
    return currentWorkspace.activeKarteSchemaSeries
      .map((schemaSeries) => {
        if (!schemaSeries.activeKarteSchema) {
          return null;
        }

        return {
          id: schemaSeries.id,
          name: schemaSeries.name,
          activeKarteSchema: {
            id: schemaSeries.activeKarteSchema.id,
            schema: unsafeValidateKarteSchema(
              schemaSeries.activeKarteSchema.schema,
            ),
          },
          primary: schemaSeries.primary,
        };
      })
      .filter((schemaSeries) => schemaSeries != null);
  }, [currentWorkspace.activeKarteSchemaSeries]);

  const primaryKarteSchemaSeries = useMemo(() => {
    return karteSchemaSeries.find((schemaSeries) => schemaSeries.primary);
  }, [karteSchemaSeries]);

  if (
    !customerFieldsSchema ||
    !customerVisitFieldsSchema ||
    !primaryKarteSchemaSeries
  ) {
    return (
      <ErrorScreen message="サービスの設定が完了していません。サポートまでお問い合わせください。" />
    );
  }

  const aiApps = currentWorkspace.aiApps
    .map((app) => {
      if (app.appId === "messaging" && app.activeConfig) {
        return {
          id: app.id,
          enabled: app.enabled,
          appId: app.appId,
          activeConfig: {
            id: app.id,
            config: unsafeValidateMessagingConfigSchema(
              app.activeConfig.config,
            ),
          },
        };
      }
      if (app.appId === "chat" && app.activeConfig) {
        return {
          id: app.id,
          enabled: app.enabled,
          appId: app.appId,
          activeConfig: {
            id: app.id,
            config: unsafeValidateChatConfigSchema(app.activeConfig.config),
          },
        };
      }
      if (app.appId === "document" && app.activeConfig) {
        return {
          id: app.id,
          enabled: app.enabled,
          appId: app.appId,
          activeConfig: {
            id: app.id,
            config: unsafeValidateDocumentConfigSchema(app.activeConfig.config),
          },
        };
      }
      return null;
    })
    .filter((app) => app != null);

  const globals: ValidatedWorkspaceGlobals = {
    salesPartner: currentWorkspace.salesPartner ?? null,
    activeCustomerFieldsSchema: customerFieldsSchema,
    activeCustomerVisitFieldsSchema: customerVisitFieldsSchema,
    primaryKarteSchemaSeries: primaryKarteSchemaSeries,
    activeKarteSchemaSeries: karteSchemaSeries,
    config: currentWorkspace.config,
    aiApps: aiApps,
    shopStaffs: currentWorkspace.shopStaffs,
    lineConnectionSetting: currentWorkspace.lineConnectionSetting ?? null,
    esConnectionSetting: currentWorkspace.esConnectionSetting ?? null,
    esSyncConnection: currentWorkspace.esSyncConnection ?? null,
    kintoneSyncConnection: currentWorkspace.kintoneSyncConnection ?? null,
    questionnaires: currentWorkspace.questionnaires,
  };

  return (
    <CurrentWorkspaceGlobalsProvider globals={globals}>
      <UITextProvider
        uiTextDictionaryId={
          isValidUITextDictionaryId(currentWorkspace.config.uiTextDictionaryId)
            ? currentWorkspace.config.uiTextDictionaryId
            : "default"
        }
      >
        {children}
      </UITextProvider>
    </CurrentWorkspaceGlobalsProvider>
  );
};
